import { useCallback, useDebugValue } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AsyncEntity } from '../../global';
import { RootState } from '../../store';
import {
  createFirmware as createFirmwareThunk,
  deleteFirmware as deleteFirmwareThunk,
  fetchFirmware as fetchFirmwareThunk,
  fetchFirmwares as fetchFirmwaresThunk,
  firmwareSelectors,
  updateFirmware as updateFirmwareThunk,
} from './firmwares.state';
import { DraftFirmware, Firmware } from './firmwares.types';
import { AppDispatch } from '../../index';
import path from 'path';
import { useUploadFileToBucket } from '../../hooks/useUploadFileToBucket';
import { useRootState } from '../../store/hooks/useRootState';

export type UseFirmwares = () => [
  { firmwares: Firmware[]; isPending: boolean },
  {
    fetchFirmwares: () => void;
    createNewFirmware: (params: { firmware: DraftFirmware; file: File | null; md5: string }) => void;
    progress: number;
  }
];

export const useFirmwares: UseFirmwares = () => {
  const dispatch: AppDispatch = useDispatch();
  const [progress, uploadFirmware] = useUploadFileToBucket();
  console.log(progress);
  const firmwares = useFirmwaresValues();
  const { isPending } = useFirmwaresState();

  const fetchFirmwares = useCallback(() => {
    dispatch(fetchFirmwaresThunk({}));
  }, [dispatch]);

  const createNewFirmware = useCallback<ReturnType<UseFirmwares>[1]['createNewFirmware']>(
    async ({ firmware, file, md5 }) => {
      if (!file) {
        throw new Error('Make sure you pass proper firmware file');
      }

      const response = await dispatch(createFirmwareThunk({ ...firmware, filename: file.name }));

      if (createFirmwareThunk.fulfilled.match(response)) {
        const { id, presignedData } = response.payload;
        const { fields, url } = presignedData;
        const { key } = fields;

        await uploadFirmware({
          file,
          fields,
          url,
        });

        const name = path.basename(key, path.extname(key));
        const uploadedAt = new Date().toISOString();

        dispatch(
          updateFirmwareThunk({
            id,
            data: {
              url: `${url}/${key}`,
              name,
              md5: md5,
              uploadedAt,
            },
          })
        );
      }
    },
    [dispatch, uploadFirmware]
  );

  return [
    { firmwares, isPending },
    { fetchFirmwares, createNewFirmware, progress },
  ];
};

export const useFirmwaresState = (): RootState['firmwares'] => {
  const firmwares = useSelector<RootState, RootState['firmwares']>(state => state.firmwares);

  useDebugValue(firmwares);

  return firmwares;
};

export const useFirmwaresValues = (): Firmware[] => {
  const firmwares = useSelector<RootState, RootState['firmwares']>(state => state.firmwares);

  const result = firmwareSelectors.selectAll(firmwares);

  useDebugValue(result);

  return result;
};

export type UseFirmwareById = (params: { id: number }) => [
  AsyncEntity<Firmware> | null,
  {
    fetchFirmware: () => void;
    updateFirmware: (params: { firmware: Partial<Firmware> }) => void;
    deleteFirmware: () => void;
  }
];

export const useFirmwareById: UseFirmwareById = ({ id }) => {
  const dispatch: AppDispatch = useDispatch();
  const state = useRootState();

  const result = firmwareSelectors.selectById(state.firmwares, id) || null;

  const fetchFirmware = useCallback(() => {
    return dispatch(fetchFirmwareThunk({ id }));
  }, [dispatch, id]);

  const deleteFirmware = useCallback(() => {
    return dispatch(deleteFirmwareThunk({ id }));
  }, [dispatch, id]);

  const updateFirmware = useCallback<ReturnType<UseFirmwareById>[1]['updateFirmware']>(
    ({ firmware }) => {
      return dispatch(updateFirmwareThunk({ id, data: firmware }));
    },
    [dispatch, id]
  );

  useDebugValue(result);

  return [result, { fetchFirmware, updateFirmware, deleteFirmware }];
};
